import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { get } from 'global/lib/lodash';
import { CardActions, CardContent, Button, Typography, DialogContent, DialogActions, DialogContentText } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import PageTitle from 'global/components/PageTitle';
import {
  HomeContent,
  HomeIconBox,
  HomeTitle,
  HomeSectionHeader,
  HomeFlex,
  HomeCard,
  HomeCardAction,
  HomeFlexLabel,
  HomeDialog,
  HomeFlexTitle,
  HomeActionStepper,
} from './Home.styles';
import { welcomeItems, homeItems } from './homeItems';
import useNavSettings from 'global/hooks/useNavSettings';
import { setHasShownWelcome } from 'redux/actions/view';
import { useUser } from 'global/hooks/useUser';
import { Text } from 'global/components/text';

export default function Home(props) {
  const [open, setOpen] = React.useState(false);
  const [activeStep, setActiveStep] = React.useState(0);
  const { nav } = useNavSettings();

  const { me } = useUser();
  const hasShownWelcome = useSelector((state) => get(state, 'view.hasShownWelcome'));
  const ProfileLink = (
    <Typography data-testid="home-button-profile-link" variant="button" component={Link} to={`/profile`}>
      Set your name
    </Typography>
  );
  const siteDocs = homeItems({ nav }).filter((f) => f.canDisplay());
  const welcomeDocs = welcomeItems({ nav });
  const maxSteps = siteDocs.length + 2;
  const dispatch = useDispatch();

  useEffect(() => {
    if (get(me, 'first_login') === 1 && !hasShownWelcome) {
      handleClickOpen(-2);
      dispatch(setHasShownWelcome());
    }
  }, [me]);

  const handleClickOpen = (step) => {
    setActiveStep(step + 2);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  useEffect(() => props.changeTitle(<PageTitle Icon={HomeIcon} title="Home" />), []);

  return (
    <HomeContent>
      <HomeTitle as={Text} variant="h5">
        <HomeIconBox as={HomeIcon} />
        Home
      </HomeTitle>
      <HomeSectionHeader as={Text} variant="h6">
        {me !== undefined ? (
          me.name === me.email ? (
            <>
              Welcome {me.email}!<>{ProfileLink}</>
            </>
          ) : (
            `Welcome back ${me.name}!`
          )
        ) : (
          `Welcome`
        )}
      </HomeSectionHeader>
      <HomeFlex>
        {siteDocs
          .filter((ni) => ni.canDisplay())
          .map((x, i) => (
            <HomeCard key={i}>
              <HomeCardAction data-testid={`home-card-action-${get(x, 'title')}`} component={Link} to={x.link}>
                <CardContent>
                  <HomeFlexLabel variant="h5" color="primary" gutterBottom>
                    {x.label}
                  </HomeFlexLabel>
                  <Typography variant="body1" component="div">
                    {x.text}
                  </Typography>
                </CardContent>
              </HomeCardAction>
              <CardActions>
                <Button onClick={() => handleClickOpen(i)} size="small" color="secondary">
                  Learn More
                </Button>
              </CardActions>
            </HomeCard>
          ))}
      </HomeFlex>
      <HomeDialog scroll="paper" maxWidth="sm" fullWidth={true} open={open} onClose={handleClose} aria-labelledby="learn-more-docs">
        <HomeFlexTitle id="learn-more-docs">
          {activeStep >= 2 ? siteDocs[activeStep - 2].label : welcomeDocs[activeStep].label}
        </HomeFlexTitle>
        <DialogContent>
          <DialogContentText color="textPrimary">{activeStep >= 2 ? siteDocs[activeStep - 2].longText : welcomeDocs[activeStep].longText}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <HomeActionStepper
            steps={maxSteps}
            position="static"
            variant="dots"
            activeStep={activeStep}
            nextButton={
              <Button size="small" onClick={handleNext} disabled={activeStep === maxSteps - 1}>
                Next
                {<KeyboardArrowRight />}
              </Button>
            }
            backButton={
              <Button size="small" onClick={handleBack} disabled={activeStep === 0}>
                {<KeyboardArrowLeft />}
                Back
              </Button>
            }
          />
        </DialogActions>
      </HomeDialog>
    </HomeContent>
  );
}
